import type { ApolloClient } from '@apollo/client';
import { ProjectAdobe } from '@frameio/graphql-client/generated';
import { ESM_ERROR_CODES } from '../constants';
import TEXT from './generated/TEXT/hasEsmProjectError';

TEXT.DEFINE({
  /** Error indicating that the asset name is invalid for a project stored in ESM */
  assetNameInvalid: 'Asset name cannot end with a space or period.',
  /** Error indicating that the asset name is forbidden for a project stored in ESM */
  assetNameForbidden:
    'Asset name cannot contain the following characters:  / * ? " | : < >"',
  /** Error indicating that the asset name is reserved for a project stored in ESM */
  assetNameReserved: ({ value }: { value: string }) =>
    `Asset name cannot be a reserved name: ${value}`,
  /** Error indicating that the asset name is duplicate for a project stored in ESM */
  assetNameDuplicate:
    'The asset name is already in use. Please choose a different name.',
  /** Error indicating that the asset name length is invalid for a project stored in ESM */
  assetNameLength: 'Asset name must be between 1 and 255 characters long.',
});

export function isAdobeProject(
  client: ApolloClient<object>,
  projectId: string
) {
  const cacheId = client.cache.identify({
    id: projectId,
    __typename: 'Project',
  });
  return !!client.cache.readFragment<{ adobeAssetId?: string }>({
    id: cacheId,
    fragment: ProjectAdobe,
  })?.adobeAssetId;
}

export function hasEsmProjectError(
  client: ApolloClient<object>,
  projectId: string,
  errorCode: string,
  value?: string
) {
  if (!isAdobeProject(client, projectId)) return null;
  const {
    NAME_INVALID,
    DUPLICATE_NAME,
    NAME_FORBIDDEN,
    NAME_LENGTH,
    NAME_RESERVED,
  } = ESM_ERROR_CODES;
  switch (errorCode) {
    case NAME_INVALID:
      return TEXT.assetNameInvalid;
    case NAME_FORBIDDEN:
      return TEXT.assetNameForbidden;
    case DUPLICATE_NAME:
      return TEXT.assetNameDuplicate;
    case NAME_LENGTH:
      return TEXT.assetNameLength;
    case NAME_RESERVED: {
      return TEXT.assetNameReserved({ value: value ?? '' }) as string;
    }
    default:
      return null;
  }
}
